import { Button } from '@chakra-ui/react'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Text } from '~/components/core'
import { BannerProps } from '~/pages/Home/Banners/BannerManager'
import { useElectronUpdaterContext } from '~/providers/ElectronUpdaterContext'
import { isElectron } from '~/utils/common'
import { openInNewTab } from '~/utils/functions'

interface WrapperProps {
  $show: boolean
}

// Warning-colored (not the green "update ready" banner): this is an action the
// user/IT needs to take, not good news.
const Wrapper = styled.div<WrapperProps>`
  height: ${({ $show }) => ($show ? '56' : '0')}px;
  padding: 0 16px;
  display: flex;
  width: 100%;
  justify-content: space-between;
  background-color: var(--chakra-colors-orange-solid);
  align-items: center;
  overflow: hidden;
  transition: height 200ms ease;
`

const TextWrapper = styled.div`
  display: flex;
  align-items: center;
`

// Persistent notice shown when the desktop app repeatedly fails to apply an
// update because something on the machine (typically antivirus/EDR) is blocking
// the staged copy. The user can't resolve that from inside the app, so we
// surface it plainly and offer a manual reinstall — which uses a different,
// un-blocked install path (Gatekeeper + file copy, not the in-place ShipIt swap).
// Intentionally NOT dismissible: the machine is stranded on an old version and
// silently hiding it is how it went unnoticed in the first place.
const UpdateBlockedBanner = ({ onShowCallback }: BannerProps) => {
  const { t } = useTranslation()
  const { isUpdateBlocked, updateBlockedDownloadUrl } =
    useElectronUpdaterContext()

  const show = isElectron && isUpdateBlocked

  useEffect(() => {
    onShowCallback(show)
  }, [show, onShowCallback])

  const handleDownload = () => {
    if (updateBlockedDownloadUrl) {
      openInNewTab(updateBlockedDownloadUrl)
    }
  }

  return (
    <Wrapper $show={show}>
      <TextWrapper>
        <Text color="var(--chakra-colors-orange-contrast)">
          {t('updateBlockedBanner.message')}
        </Text>
        {updateBlockedDownloadUrl && (
          <Button
            marginLeft=".5rem"
            onClick={handleDownload}
            bg="bg.panel"
            color="fg.default"
            borderRadius="full"
            _hover={{ bg: 'bg.muted' }}
            size="sm"
          >
            {t('updateBlockedBanner.downloadLatest')}
          </Button>
        )}
      </TextWrapper>
    </Wrapper>
  )
}

export default UpdateBlockedBanner
